import { FilterOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import type { Dayjs } from "dayjs";
import {
  Form,
  Input,
  InputNumber,
  Button,
  Select,
  type FormProps,
  Flex,
  theme,
  Radio,
  DatePicker,
  CheckboxProps,
  Badge,
  Typography,
  Spin,
} from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import { CrudFilters, useGetIdentity } from "@refinedev/core";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import { getStatuses } from "@/utilities";
import {
  CustomersFilter,
  OperatorsFilter,
  parseCustomersFilterValue,
  parseOperatorsFilterValue,
} from "@/components";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import esES from "antd/es/date-picker/locale/es_ES";

const { RangePicker } = DatePicker;
const { Text } = Typography;

export const getPaymentStatuses = (t: TFunction) => [
  { value: "pending", label: t("common.paymentStatus.pending") },
  { value: "review", label: t("common.paymentStatus.review") },
  { value: "verified", label: t("common.paymentStatus.verified") },
  { value: "invoiced", label: t("common.paymentStatus.invoiced") },
  { value: "canceled", label: t("common.paymentStatus.canceled") },
];

export const getDeliveryStatuses = (t: TFunction) => [
  { value: "pending", label: t("common.deliveryStatus.pending") },
  { value: "preparing", label: t("common.deliveryStatus.preparing") },
  {
    value: "ready_to_deliver",
    label: t("common.deliveryStatus.readyToDeliver"),
  },
  { value: "in_transit", label: t("common.deliveryStatus.inTransit") },
  { value: "delivered", label: t("common.deliveryStatus.delivered") },
  { value: "pending_cancel", label: t("common.deliveryStatus.pendingCancel") },
  { value: "canceled", label: t("common.deliveryStatus.canceled") },
];

export const getPaymentMethods = (t: TFunction) => [
  {
    label: t("common.paymentMethod.bankTransfer"),
    value: "banktransfer",
  },
  { label: t("common.paymentMethod.eCheq"), value: "checkmo" },
  { label: t("common.paymentMethod.debitOrCreditCard"), value: "firstdata" },
  {
    label: t("common.paymentMethod.mercadoPago"),
    value: "mercadopago_basic",
  },
  { label: t("common.paymentMethod.inCash"), value: "purchaseorder" },
  { label: t("common.paymentMethod.getNet"), value: "argenmagento" },
];

export const getDeliveryMethods = (t: TFunction) => [
  { value: "zippin", label: t("common.deliveryMethod.paqueteria") },
  {
    value: "marketplaceshipping",
    label: t("common.deliveryMethod.fleteConFlotaPropia"),
  },
  { value: "storepickup", label: t("common.deliveryMethod.retiroEnDeposito") },
  // Sin "Nota de crédito": no es una forma de envío, y buscaba `credit_memo`, un valor que el
  // admin no usa —la entrega de una cancelación se crea como `canceled`—. Los pedidos con
  // cancelaciones se filtran con Estado de entrega → Cancelado (Diseño, 2026-10-19).
];

export const OrdersFilters: React.FC<{
  searchFormProps: FormProps;
  filters?: CrudFilters;
  sellersSelectProps?: any;
  storesSelectProps?: any;
  selectedStores: any;
  t?: TFunction;
}> = ({
  searchFormProps,
  filters,
  sellersSelectProps,
  storesSelectProps,
  selectedStores,
  t: translateProp,
}) => {
    const { t: tHook } = useTranslation();
    const t = translateProp ?? tHook;
    const paymentStatuses = getPaymentStatuses(t);
    const deliveryStatuses = getDeliveryStatuses(t);
    const paymentMethods = getPaymentMethods(t);
    const deliveryMethods = getDeliveryMethods(t);
    const [calendarValue, setCalendarValue] = useState<any>();
    const [showRangePicker, setShowRangePicker] = useState(false);
    const [radioValue, setRadioValue] = useLocalStorage<any>("RadioCalendar", []);
    const [calendarValueCreated, setCalendarValueCreated] = useState<any>();
    const [showRangePickerCreated, setShowRangePickerCreated] = useState(false);
    const [status, setStatus] = useState("");
    const [radioValueCreated, setRadioValueCreated] = useLocalStorage<any>(
      "RadioCalendarCreated",
      [],
    );
    const [radioValueParticipant, setRadioValueParticipant] = useState<
      "all" | "me"
    >("all");

    const { data: identity } = useGetIdentity<{ email?: string }>();
    const STATUSES = getStatuses(t);
    const { token } = theme.useToken();

    // Al abrir el drawer con filtros aplicados que el formulario no conoce (URL, o drawer destruido),
    // se rehidratan desde los ids; los nombres no viajan en la URL.
    useEffect(() => {
      const form = searchFormProps?.form;
      const ops = filters?.find((x: any) => x.field === "operators");
      const actualOps = form?.getFieldValue("operators");
      if (ops?.value && !(Array.isArray(actualOps) && actualOps.length)) {
        form?.setFieldsValue({ operators: parseOperatorsFilterValue(ops.value) });
      }
      const cus = filters?.find((x: any) => x.field === "customers");
      const actualCus = form?.getFieldValue("customers");
      if (cus?.value && !(Array.isArray(actualCus) && actualCus.length)) {
        form?.setFieldsValue({ customers: parseCustomersFilterValue(cus.value) });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    const participantPresets = useMemo(
      () => [
        { label: t("filter.participant.all"), value: "all" as const },
        {
          label: t("filter.participant.me"),
          value: "me" as const,
        },
      ],
      [t],
    );

    const handleRadioChangeParticipant = (e: any) => {
      const val = e.target.value as "all" | "me";
      setRadioValueParticipant(val);

      if (val === "me" && identity?.email) {
        searchFormProps.form?.setFieldsValue({
          participant_toggle: identity.email,
        });
      } else {
        searchFormProps.form?.setFieldsValue({ participant_toggle: undefined });
      }
    };

    useEffect(() => {
      const f = filters?.find((x: any) => x.field === "participations.email");
      if (
        f?.value &&
        identity?.email &&
        String(f.value).toLowerCase() === String(identity.email).toLowerCase()
      ) {
        setRadioValueParticipant("me");
        searchFormProps.form?.setFieldsValue({
          participant_toggle: identity.email,
        });
      } else {
        setRadioValueParticipant("all");
        searchFormProps.form?.setFieldsValue({ participant_toggle: undefined });
      }
    }, [filters, identity?.email]);

    const handleRangeChange = (dates: null | (Dayjs | null)[]) => {
      if (dates) {
        searchFormProps.form?.setFieldsValue({ calendar: dates });
      } else {
        searchFormProps.form?.setFieldsValue({ calendar: undefined });
      }
    };

    const handleRangeChangeCreated = (dates: null | (Dayjs | null)[]) => {
      if (dates) {
        searchFormProps.form?.setFieldsValue({ calendar_created: dates });
      } else {
        searchFormProps.form?.setFieldsValue({ calendar_created: undefined });
      }
    };

    useEffect(() => {
      searchFormProps.form?.setFieldsValue({ calendar: calendarValue });
    }, [calendarValue]);

    useEffect(() => {
      searchFormProps.form?.setFieldsValue({
        calendar_created: calendarValueCreated,
      });
    }, [calendarValueCreated]);

    const thisToday: [Dayjs, Dayjs] = [
      dayjs().startOf("day"),
      dayjs().endOf("day"),
    ];

    const thisWeek: [Dayjs, Dayjs] = [
      dayjs().startOf("week"),
      dayjs().endOf("week"),
    ];
    const thisMonth: [Dayjs, Dayjs] = [
      dayjs().startOf("month"),
      dayjs().endOf("month"),
    ];

    const rangePresets = useMemo(
      () => [
        { label: t("filter.calendar.all"), value: "" },
        { label: t("filter.calendar.today"), value: "today" },
        { label: t("filter.calendar.thisWeek"), value: "thisweek" },
        { label: t("filter.calendar.thisMonth"), value: "thismonth" },
        { label: t("filter.calendar.personalized"), value: "personalized" },
      ],
      [t],
    );

    useEffect(() => {
      if (filters) {
        /* @ts-expect-error Ant Design Icon's v5.0.1 has an issue with @types/react@^18.2.66 */
        const calendarFilter = filters?.find((f) => f.field === "updatedAt");
        const calendarFilterCreated = filters?.find(
          /* @ts-expect-error Ant Design Icon's v5.0.1 has an issue with @types/react@^18.2.66 */
          (f) => f.field === "createdAt",
        );

        if (calendarFilter) {
          const [start, end] = calendarFilter.value || [];
          if (start && end) {
            setCalendarValue([dayjs(start), dayjs(end)]);
          } else {
            setCalendarValue(undefined);
            setRadioValue("");
            setShowRangePicker(false);
          }
        }

        if (calendarFilterCreated) {
          const [start, end] = calendarFilterCreated.value || [];
          if (start && end) {
            setCalendarValueCreated([dayjs(start), dayjs(end)]);
          } else {
            setCalendarValueCreated(undefined);
            setRadioValueCreated("");
            setShowRangePickerCreated(false);
          }
        }
      }
    }, [filters]);

    const handleRadioChange = useCallback(
      (e: any) => {
        const { value } = e.target;
        setRadioValue(value);

        if (value === "personalized") {
          setShowRangePicker(true);
          setCalendarValue(value);
        } else if (value === "today") {
          setShowRangePicker(false);
          setCalendarValue(thisToday);
        } else if (value === "thisweek") {
          setShowRangePicker(false);
          setCalendarValue(thisWeek);
        } else if (value === "thismonth") {
          setShowRangePicker(false);
          setCalendarValue(thisMonth);
        } else {
          setShowRangePicker(false);
          setCalendarValue(undefined);
        }
      },
      [setRadioValue, setShowRangePicker, setCalendarValue],
    );

    const handleRadioChangeCreated = useCallback(
      (e: any) => {
        const { value } = e.target;
        setRadioValueCreated(value);

        if (value === "personalized") {
          setShowRangePickerCreated(true);
          setCalendarValueCreated(value);
        } else if (value === "today") {
          setShowRangePickerCreated(false);
          setCalendarValueCreated(thisToday);
        } else if (value === "thisweek") {
          setShowRangePickerCreated(false);
          setCalendarValueCreated(thisWeek);
        } else if (value === "thismonth") {
          setShowRangePickerCreated(false);
          setCalendarValueCreated(thisMonth);
        } else {
          setShowRangePickerCreated(false);
          setCalendarValueCreated(undefined);
        }
      },
      [setRadioValueCreated, setShowRangePickerCreated, setCalendarValueCreated],
    );

    function clearFilters() {
      searchFormProps.form?.resetFields();
      setCalendarValue(undefined);
      setCalendarValueCreated(undefined);
      setRadioValue("");
      setRadioValueCreated("");
      setShowRangePicker(false);

      setRadioValueParticipant("all");
      searchFormProps.form?.setFieldsValue({ participant_toggle: undefined });

      searchFormProps.form?.submit();
    }

    const toggleChecked = (value: string) => {
      const newStatus = status === value ? "" : value;
      setStatus(newStatus);
      searchFormProps.form?.setFieldsValue({ orderStatus: newStatus });
    };


    return (
      <Form layout="vertical" {...searchFormProps} style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}>
        <div style={{ flex: 1, overflowY: "auto", padding: "16px 24px" }}>
        <Form.Item name="participant_toggle" hidden>
          <Input />
        </Form.Item>

        <Form.Item
          label={
            <span style={{ color: token.colorTextTertiary }}>
              {t("filter.participant.title", "Participants")}
            </span>
          }
        >
          <Flex vertical>
            <Radio.Group
              value={radioValueParticipant}
              style={{ display: "flex", gap: 10 }}
              size="small"
              onChange={handleRadioChangeParticipant}
            >
              {participantPresets.map((preset, i) => (
                <Radio.Button key={`${i}-participant`} value={preset.value}>
                  {preset.label}
                </Radio.Button>
              ))}
            </Radio.Group>
          </Flex>
        </Form.Item>

        <Form.Item
          label={
            <span style={{ color: token.colorTextTertiary }}>
              {t("orders.table.status")}
            </span>
          }
          name="orderStatus"
        >
          <Flex gap={16}>
            <Button
              type={status === "open" ? "primary" : "default"}
              size="small"
              onClick={() => toggleChecked("open")}
            >
              <Badge color="orange" /> {t("quotes.status.Open")}
            </Button>

            <Button
              type={status === "complete" ? "primary" : "default"}
              size="small"
              onClick={() => toggleChecked("complete")}
            >
              <Badge color="green" /> {t("orders.status.complete")}
            </Button>

            <Button
              type={status === "cancel" ? "primary" : "default"}
              size="small"
              onClick={() => toggleChecked("cancel")}
            >
              <Badge color="red" /> {t("orders.status.cancel")}
            </Button>
          </Flex>
        </Form.Item>

        <Form.Item
          label={
            <span style={{ color: token.colorTextTertiary }}>
              {t("filter.calendar.lastCreate")}
            </span>
          }
          name="calendar_created"
          key="calendar_created_unique"
        >
          <Flex vertical>
            <Radio.Group
              value={radioValueCreated}
              style={{
                display: "flex",
                gap: "10px",
                marginBottom: showRangePickerCreated ? "10px" : "",
              }}
              size="small"
              onChange={handleRadioChangeCreated}
            >
              {rangePresets.map((preset, i) => (
                <Radio.Button
                  key={`${i}-created`}
                  value={preset.value}
                  name={preset.value}
                >
                  {preset.label}
                </Radio.Button>
              ))}
            </Radio.Group>
            {showRangePickerCreated && (
              <RangePicker locale={esES} onChange={handleRangeChangeCreated} required />
            )}
          </Flex>
        </Form.Item>

        <Form.Item
          label={
            <span style={{ color: token.colorTextTertiary }}>
              {t("filter.calendar.lastUpdate")}
            </span>
          }
          name="calendar"
          key="calendar_updated"
        >
          <Flex vertical>
            <Radio.Group
              value={radioValue}
              style={{
                display: "flex",
                gap: "10px",
                marginBottom: showRangePicker ? "10px" : "",
              }}
              size="small"
              onChange={handleRadioChange}
            >
              {rangePresets.map((preset, i) => (
                <Radio.Button key={i} value={preset.value} name={preset.value}>
                  {preset.label}
                </Radio.Button>
              ))}
            </Radio.Group>
            {showRangePicker && (
              <RangePicker onChange={handleRangeChange} required />
            )}
          </Flex>
        </Form.Item>

        <Form.Item
          label={<span style={{ color: token.colorTextTertiary }}>Sellers</span>}
          name="sellerCode"
        >
          <Flex vertical>
            <Select
              allowClear
              mode="multiple"
              placeholder={t("filter.calendar.all")}
              options={sellersSelectProps?.map((option: any) => ({
                label: option.name as string,
                value: option.code as string,
              }))}
              defaultValue={
                // @ts-expect-error Ant Design Icon's v5.0.1 has an issue with @types/react@^18.2.66
                filters?.find((f) => f.field === "sellerCode")?.value
              }
              onChange={(value) => {
                searchFormProps.form?.setFieldsValue({ sellerCode: value });
              }}
            />
          </Flex>
        </Form.Item>

        {/* Fase 7 de filtros-operadores-clientes (xcons-docs#308): Operadores por afiliación y
            Clientes en lista, ambos acotados al store elegido arriba en cada render. */}
        <Form.Item
          label={
            <span style={{ color: token.colorTextTertiary }}>
              {t("filter.operators.title")}
            </span>
          }
          name="operators"
        >
          <OperatorsFilter
            storeCodes={(selectedStores ?? [])
              .map((s: any) => s?.code)
              .filter(Boolean)}
          />
        </Form.Item>

        <Form.Item
          label={
            <span style={{ color: token.colorTextTertiary }}>
              {t("filter.customers.title")}
            </span>
          }
          name="customers"
        >
          <CustomersFilter
            storeCodes={(selectedStores ?? [])
              .map((s: any) => s?.code)
              .filter(Boolean)}
          />
        </Form.Item>

        {/* <Flex gap={8}>
        <Form.Item
          label={
            <span style={{ color: token.colorTextTertiary }}>Orders range</span>
          }
          name="price_product_from"
        >
          <InputNumber<number>
            min={0}
            style={{ width: "100%" }}
            placeholder={t("filter.products.labelPriceFrom")}
          />
        </Form.Item>
        <Form.Item
          label={
            <span style={{ color: "white" }}>
              {t("filter.products.labelPrice")}
            </span>
          }
          name="price_product_to"
        >
          <InputNumber<number>
            min={0}
            style={{ width: "100%" }}
            placeholder={t("filter.products.labelPriceTo")}
          />
        </Form.Item>
      </Flex> */}

        <Form.Item
          label={
            <span style={{ color: token.colorTextTertiary }}>
              {t("common.payment.method")}
            </span>
          }
          name="paymentMethod"
        >
          <Flex vertical>
            <Select
              allowClear
              placeholder={t("filter.calendar.all")}
              options={paymentMethods}
              defaultValue={
                // @ts-expect-error Ant Design Icon's v5.0.1 has an issue with @types/react@^18.2.66
                filters?.find((f) => f.field === "payments.paymentMethod")?.value
              }
              onChange={(value) => {
                searchFormProps.form?.setFieldsValue({ paymentMethod: value });
              }}
            />
          </Flex>
        </Form.Item>

        <Form.Item
          label={
            <span style={{ color: token.colorTextTertiary }}>
              {t("common.payment.status")}
            </span>
          }
          name="paymentStatus"
        >
          <Flex vertical>
            <Select
              allowClear
              placeholder={t("filter.calendar.all")}
              options={paymentStatuses}
              defaultValue={
                // @ts-expect-error Ant Design Icon's v5.0.1 has an issue with @types/react@^18.2.66
                filters?.find((f) => f.field === "payments.status")?.value
              }
              onChange={(value) => {
                searchFormProps.form?.setFieldsValue({ paymentStatus: value });
              }}
            />
          </Flex>
        </Form.Item>

        <Form.Item
          label={
            <span style={{ color: token.colorTextTertiary }}>
              {t("orders.label.deliverymethod")}
            </span>
          }
          name="deliveryMethod"
        >
          <Flex vertical>
            <Select
              allowClear
              placeholder={t("filter.calendar.all")}
              options={deliveryMethods}
              defaultValue={
                // @ts-expect-error Ant Design Icon's v5.0.1 has an issue with @types/react@^18.2.66
                filters?.find((f) => f.field === "shipments.deliveryMethod")
                  ?.value
              }
              onChange={(value) => {
                searchFormProps.form?.setFieldsValue({ deliveryMethod: value });
              }}
            />
          </Flex>
        </Form.Item>

        <Form.Item
          label={
            <span style={{ color: token.colorTextTertiary }}>
              {t("orders.table.deliveryStatus")}
            </span>
          }
          name="deliveryStatus"
        >
          <Flex vertical>
            <Select
              allowClear
              placeholder={t("filter.calendar.all")}
              options={deliveryStatuses}
              defaultValue={
                // @ts-expect-error Ant Design Icon's v5.0.1 has an issue with @types/react@^18.2.66
                filters?.find((f) => f.field === "shipments.status")?.value
              }
              onChange={(value) => {
                searchFormProps.form?.setFieldsValue({ deliveryStatus: value });
              }}
            />
          </Flex>
        </Form.Item>

        </div>
        <Flex
          align="center"
          justify="end"
          gap="8px"
          style={{ flexShrink: 0, padding: "12px 24px", borderTop: "1px solid #f0f0f0", backgroundColor: "#fff" }}
        >
          <Button type="default" danger onClick={clearFilters}>
            {t("filter.button.clearFilters")}
          </Button>
          <Button
            htmlType="submit"
            type="primary"
            iconPosition="end"
            icon={<FilterOutlined />}
          >
            {t("filter.button.filter")}
          </Button>
        </Flex>
      </Form>
    );
  };
